import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import { useState } from 'react';

export default function SubtitleTimeShiftButtons({
    index,
    subtitle,
    setSubtitle,
    timeToSeconds,
    secondsToTime,
    readOnly,
}) {
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [shiftDirection, setShiftDirection] = useState('');
    const [minutes, setMinutes] = useState('0');
    const [seconds, setSeconds] = useState('0');
    const [milliseconds, setMilliseconds] = useState('0');

    const handleOpenDialog = (direction) => {
        if (readOnly) return;
        setShiftDirection(direction);
        setMinutes('0');
        setSeconds('0');
        setMilliseconds('0');
        setIsDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setIsDialogOpen(false);
    };

    const handleShiftTimes = () => {
        // Convert input values to numbers
        const minutesValue = parseFloat(minutes) || 0;
        const secondsValue = parseFloat(seconds) || 0;
        const millisecondsValue = parseFloat(milliseconds) || 0;

        // Calculate total shift in seconds
        const shiftValue = minutesValue * 60 + secondsValue + millisecondsValue / 1000;

        if (shiftValue <= 0) {
            alert('Please enter a time value greater than 0');
            return;
        }

        // Convert to seconds (negative for shifting up/earlier)
        const shiftInSeconds = shiftDirection === 'up' ? -shiftValue : shiftValue;

        // Create a new subtitle array with adjusted times
        const newSubtitle = subtitle.map((sub) => ({ ...sub }));

        // Shift all subtitles from this index onwards
        for (let i = index; i < newSubtitle.length; i++) {
            const sub = newSubtitle[i];

            // Process start time
            const startSeconds = timeToSeconds(sub.start) + shiftInSeconds;
            if (startSeconds < 0) {
                alert(`Cannot shift times before 00:00:00. Aborting.`);
                handleCloseDialog();
                return;
            }
            sub.start = secondsToTime(startSeconds);

            // Process end time
            const endSeconds = timeToSeconds(sub.end) + shiftInSeconds;
            if (endSeconds < 0) {
                alert(`Cannot shift times before 00:00:00. Aborting.`);
                handleCloseDialog();
                return;
            }
            sub.end = secondsToTime(endSeconds);

            // Handle Estart and Eend if they exist
            if (sub.Estart) {
                const estartSeconds = timeToSeconds(sub.Estart) + shiftInSeconds;
                if (estartSeconds >= 0) {
                    sub.Estart = secondsToTime(estartSeconds);
                }
            }

            if (sub.Eend) {
                const eendSeconds = timeToSeconds(sub.Eend) + shiftInSeconds;
                if (eendSeconds >= 0) {
                    sub.Eend = secondsToTime(eendSeconds);
                }
            }
        }

        setSubtitle(newSubtitle);
        handleCloseDialog();
    };

    return (
        <div
            className="subtitle-time-shift-buttons"
            style={{ display: 'flex', flexDirection: 'column', margin: '0 4px' }}
        >
            <Tooltip
                title={readOnly ? 'Read-only: time shifting is locked' : 'Move timing earlier for this and all subtitles below'}
                placement="left"
            >
                <span>
                    <IconButton
                        size="small"
                        disabled={readOnly}
                        onClick={() => handleOpenDialog('up')}
                        style={{ padding: '2px', color: '#666' }}
                    >
                        <ArrowUpwardIcon fontSize="small" />
                    </IconButton>
                </span>
            </Tooltip>

            <Tooltip
                title={readOnly ? 'Read-only: time shifting is locked' : 'Move timing later for this and all subtitles below'}
                placement="left"
            >
                <span>
                    <IconButton
                        size="small"
                        disabled={readOnly}
                        onClick={() => handleOpenDialog('down')}
                        style={{ padding: '2px', color: '#666' }}
                    >
                        <ArrowDownwardIcon fontSize="small" />
                    </IconButton>
                </span>
            </Tooltip>

            <Dialog open={isDialogOpen} onClose={handleCloseDialog}>
                <DialogTitle>
                    <Typography variant="h6">
                        {shiftDirection === 'up'
                            ? 'Shift Subtitles Earlier (⬆️ Move Time Backwards)'
                            : 'Shift Subtitles Later (⬇️ Move Time Forward)'}
                    </Typography>
                </DialogTitle>
                <DialogContent>
                    <Typography variant="subtitle1" style={{ marginTop: '10px', marginBottom: '15px' }}>
                        Enter the amount of time to shift:
                    </Typography>

                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={4}>
                            <TextField
                                autoFocus
                                label="Minutes"
                                type="number"
                                fullWidth
                                value={minutes}
                                onChange={(e) => setMinutes(e.target.value)}
                                inputProps={{ min: '0', step: '1' }}
                            />
                        </Grid>
                        <Grid item xs={4}>
                            <TextField
                                label="Seconds"
                                type="number"
                                fullWidth
                                value={seconds}
                                onChange={(e) => setSeconds(e.target.value)}
                                inputProps={{ min: '0', max: '59', step: '1' }}
                            />
                        </Grid>
                        <Grid item xs={4}>
                            <TextField
                                label="Milliseconds"
                                type="number"
                                fullWidth
                                value={milliseconds}
                                onChange={(e) => setMilliseconds(e.target.value)}
                                inputProps={{ min: '0', max: '999', step: '1' }}
                            />
                        </Grid>
                    </Grid>

                    <Typography variant="body2" style={{ marginTop: '20px', color: '#666' }}>
                        This will shift all subtitles from position {index + 1} onwards
                        {shiftDirection === 'up' ? ' earlier ' : ' later '}
                        by the specified amount of time.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog} color="primary">
                        Cancel
                    </Button>
                    <Button onClick={handleShiftTimes} color="primary">
                        Apply Shift
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
}
